// Die Avatarfarben als Daten: vier Bereiche, je eine Farbe aus einer festen Palette
// (docs/architektur-paket-2.md, Abschnitt „Avatar“). Reine Daten ohne Rendering – der Bauplan des
// Blockmodells (`avatarBauplan`) kommt in task-011 in dieselbe Ordnung.
//
// Warum eine Palette und nicht ein freier Farbwähler: Kinder sollen wählen, nicht mischen, und der
// Server kann jede eingehende Farbe gegen eine kurze Liste prüfen statt gegen einen Zahlenraum.

/** Die vier färbbaren Bereiche eines Avatars, je als `#rrggbb`. */
export interface AvatarColors {
  haut: string;
  haar: string;
  oberteil: string;
  hose: string;
}

/** Die Bereiche in fester Reihenfolge; der Spiegel in task-011 zeigt sie so untereinander. */
export const AVATAR_BEREICHE = ['haut', 'haar', 'oberteil', 'hose'] as const;

/** Auswahl je Bereich: acht bis zwölf Farben, kleingeschrieben als `#rrggbb`. */
export const AVATAR_PALETTE: Record<keyof AvatarColors, readonly string[]> = {
  haut: ['#ffdbac', '#f1c27d', '#e0ac69', '#c68642', '#a9683b', '#8d5524', '#6b4226', '#5c3317'],
  haar: [
    '#090806',
    '#2c222b',
    '#4b3621',
    '#71635a',
    '#a7856a',
    '#d6c4a8',
    '#e6cea8',
    '#8c4b2f',
    '#b55239',
    '#9a9a9a',
  ],
  oberteil: [
    '#3a6ea5',
    '#2f7d7a',
    '#2e5d3a',
    '#6b8f3a',
    '#d8c47a',
    '#c9782b',
    '#a3543f',
    '#8b2f2f',
    '#b0446c',
    '#7a4b8f',
    '#4a4a55',
    '#e8e0cc',
  ],
  hose: [
    '#5c4033',
    '#6b5b3e',
    '#8a7455',
    '#41563b',
    '#2f4858',
    '#3b3b46',
    '#1f2933',
    '#4d3a5c',
    '#7a3b2e',
    '#9c9484',
  ],
};

/** Vorbelegung für ein frisches Konto, bis der Spieler im Spiegel etwas anderes wählt. */
export const DEFAULT_AVATAR: AvatarColors = {
  haut: '#e0ac69',
  haar: '#4b3621',
  oberteil: '#3a6ea5',
  hose: '#5c4033',
};

/**
 * Prüft eingehende Avatarfarben: genau die vier Bereiche, kein Feld mehr, und je Bereich eine
 * Farbe aus `AVATAR_PALETTE`. Groß- und Kleinschreibung der Hexziffern ist egal, damit ein
 * gespeicherter Wert von früher nicht plötzlich als ungültig gilt.
 */
export function isAvatarColors(wert: unknown): wert is AvatarColors {
  if (typeof wert !== 'object' || wert === null || Array.isArray(wert)) return false;
  const eintraege = wert as Record<string, unknown>;
  if (Object.keys(eintraege).length !== AVATAR_BEREICHE.length) return false;
  return AVATAR_BEREICHE.every((bereich) => {
    const farbe = eintraege[bereich];
    return typeof farbe === 'string' && AVATAR_PALETTE[bereich].includes(farbe.toLowerCase());
  });
}
